<script lang="ts">
  // The system-wide colonies spreadsheet: the primary way to run your empire.
  // Every edit is an optimistic command; dirty cells resolve on host accept.
  import { selectors, itemLabel, itemMayRepeat, explainOutput, COLONY_TAGS, ANDROID_RACE } from '@engine/index';
  import { itemDescription } from '@engine/data/index';
  import { app, getActive, savePerGame } from '../state.svelte';
  import AutopilotBar from '../components/AutopilotBar.svelte';
  import PixelPlanet from '../PixelPlanet.svelte';
  import { leaderDisplayName } from '../leaderNames';

  const session = () => getActive()!.session;
  const allRowsWithOutposts = $derived.by(() => {
    void app.version;
    const s = session().getPlanned();
    return s ? selectors.colonyRows(s, session().playerId) : [];
  });
  // outposts are fuel stops, not economies: they live on the map, not here
  const allRows = $derived(allRowsWithOutposts.filter((r) => !r.outpost));
  const outpostCount = $derived(allRowsWithOutposts.length - allRows.length);
  const stickyMode = $derived.by(() => {
    void app.version;
    return session().getPlanned()?.settings.modes.stickyBuild === true;
  });
  // live research readout: the whole point of shuffling scientists is to see
  // what it does to research, so the numbers live HERE, not a screen away
  // (bugs.md: "move guy off research, click research screen to observe
  // percent, click back" — the flipping killed the workflow)
  const research = $derived.by(() => {
    void app.version;
    const s = session().getPlanned();
    if (!s) return null;
    const sum = selectors.empireSummary(s, session().playerId);
    const accumRP = s.empires.find((e) => e.id === session().playerId)?.research.accumRP ?? 0;
    return { sum, accumRP };
  });
  const label = (item: string) => {
    const s = session().getPlanned();
    return s ? itemLabel(s, session().playerId, item) : item;
  };
  const pretty = (id: string) => id.replaceAll('_', ' ');
  const SIZE_NAMES: Record<number, string> = { 1: 'tiny', 2: 'small', 3: 'medium', 4: 'large', 5: 'huge' };

  /** what each planet feature means for production (hover on the planet cell) */
  const MINERAL_PROD_INFO: Record<string, string> = {
    ultra_poor: '1 production per worker',
    poor: '2 production per worker',
    abundant: '3 production per worker',
    rich: '5 production per worker',
    ultra_rich: '8 production per worker',
  };
  const SPECIAL_INFO: Record<string, { icon: string; text: string }> = {
    gold_deposits: { icon: '🥇', text: 'gold deposits: +5 BC per turn' },
    gem_deposits: { icon: '💎', text: 'gem deposits: +10 BC per turn' },
    ancient_artifacts: { icon: '🏺', text: 'ancient artifacts: +2 research per scientist' },
  };
  function planetTitle(row: selectors.ColonyRow): string {
    const p = row.planet;
    const lines = [
      `${p.climate} · size ${p.sizeClass}`,
      `${pretty(p.minerals)} minerals — ${MINERAL_PROD_INFO[p.minerals] ?? ''}`,
      p.gravity === 'normal' ? 'normal gravity' : `${p.gravity} gravity — −25% output per step without a gravity generator`,
    ];
    if (p.special && SPECIAL_INFO[p.special]) lines.push(`${SPECIAL_INFO[p.special]!.icon} ${SPECIAL_INFO[p.special]!.text}`);
    return lines.join('\n');
  }

  // ---- filter + sort ----
  let filter = $state('');
  // the citizens/bulk-builds explainer shows until dismissed once (then never)
  let showCitizensTip = $state(localStorage.getItem('moo2.tip.citizens') !== '0');
  function dismissCitizensTip() {
    showCitizensTip = false;
    try {
      localStorage.setItem('moo2.tip.citizens', '0');
    } catch {
      // private mode: dismisses for this tab at least
    }
  }
  let showTags = $state(localStorage.getItem('moo2.showTags') !== '0');
  function toggleTags() {
    showTags = !showTags;
    localStorage.setItem('moo2.showTags', showTags ? '1' : '0');
  }
  type SortKey = 'name' | 'pop' | 'food' | 'prod' | 'sci' | 'bc' | 'morale' | 'building';
  let sortKey = $state<SortKey>('name');
  let sortDir = $state(1);
  function sortBy(k: SortKey) {
    if (sortKey === k) sortDir = -sortDir;
    else {
      sortKey = k;
      sortDir = k === 'name' || k === 'building' ? 1 : -1;
    }
  }
  const keyFns: Record<SortKey, (r: selectors.ColonyRow) => string | number> = {
    name: (r) => r.name,
    pop: (r) => r.popUnits,
    food: (r) => r.output.foodNet,
    prod: (r) => r.output.prodToQueue || r.output.prod,
    sci: (r) => r.output.research,
    bc: (r) => r.output.bcIncome,
    morale: (r) => r.output.moralePct,
    building: (r) => r.activeItem ?? '',
  };
  const rows = $derived.by(() => {
    let out = allRows;
    const f = filter.trim().toLowerCase();
    if (f) {
      out = out.filter(
        (r) =>
          r.name.toLowerCase().includes(f) ||
          r.starName.toLowerCase().includes(f) ||
          r.planet.climate.includes(f) ||
          (r.activeItem ?? '').includes(f) ||
          r.tags.some((t) => t.includes(f)),
      );
    }
    const fn = keyFns[sortKey];
    return [...out].sort((a, b) => {
      const x = fn(a);
      const y = fn(b);
      const c = typeof x === 'string' ? x.localeCompare(y as string) : (x as number) - (y as number);
      return c !== 0 ? c * sortDir : a.id - b.id;
    });
  });
  const totals = $derived.by(() => {
    const t = { pop: 0, growthK: 0, food: 0, prod: 0, sci: 0, bc: 0, pollution: 0 };
    for (const r of allRows) {
      if (r.outpost) continue;
      t.pop += r.popUnits;
      t.growthK += r.growthK;
      t.food += r.output.foodNet;
      t.prod += r.output.prodToQueue || r.output.prod;
      t.sci += r.output.research;
      t.bc += r.output.bcIncome;
      t.pollution += r.output.pollution;
    }
    return t;
  });
  const growthLabel = (k: number) => {
    const v = k / 1000;
    // consistent precision below 1.0: "+0.09" vs "+0.05" must not render as
    // "+0.1" vs "+0.05" (mixed precision made small dips look like halvings)
    const s = v !== 0 && Math.abs(v) < 1 ? v.toFixed(2) : v.toFixed(1);
    return `${v >= 0 ? '+' : ''}${s}`;
  };

  /** hover breakdown per output column: every coefficient and where it's from */
  function explain(rowId: number): { farm: string; prod: string; sci: string; bc: string } {
    const s = session().getPlanned();
    const c = s?.colonies.find((x) => x.id === rowId);
    if (!s || !c || c.outpost) return { farm: '', prod: '', sci: '', bc: '' };
    const ex = explainOutput(s, c);
    return {
      farm: ex.farm.join('\n'),
      prod: ex.prod.join('\n'),
      sci: ex.sci.join('\n'),
      bc: ex.bc.join('\n'),
    };
  }

  // ---- bulk ops ----
  let selected = $state<Set<number>>(new Set());
  function toggleSelect(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }
  function bulkBuild(item: string) {
    if (!item) return;
    for (const row of rows) {
      if (!selected.has(row.id) || !row.buildable.includes(item)) continue;
      const items = row.queue.length ? [item, ...row.queueEntries.slice(1)] : [item];
      submitNoted('set_build_queue', { colonyId: row.id, items });
    }
  }
  /** queue an item on every selected colony: 'front' = right after the item
   * being built (nothing invested is lost), 'back' = end of the queue */
  function bulkQueue(item: string, where: 'front' | 'back') {
    if (!item) return;
    for (const row of rows) {
      if (!selected.has(row.id) || !row.buildable.includes(item)) continue;
      const items =
        where === 'back' || row.queue.length === 0
          ? [...row.queueEntries, item]
          : [row.queueEntries[0]!, item, ...row.queueEntries.slice(1)];
      submitNoted('set_build_queue', { colonyId: row.id, items });
    }
  }
  // UNION of the selection's buildables, with a how-many-will-get-it count.
  // The old intersection hid climate-limited buildings entirely: one barren
  // (or already-enriched) colony in the selection and soil enrichment
  // vanished from the menu even for the terran/arid worlds that wanted it
  // (bugs.md). bulkBuild/bulkQueue already skip colonies that can't take
  // the item, so offering the union is safe.
  const bulkOptions = $derived.by(() => {
    const chosen = rows.filter((r) => selected.has(r.id));
    if (!chosen.length) return [];
    const counts = new Map<string, number>();
    for (const r of chosen) for (const item of r.buildable) counts.set(item, (counts.get(item) ?? 0) + 1);
    return [...counts.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([item, n]) => ({ item, n, total: chosen.length }));
  });
  const selectableRows = $derived(rows.filter((r) => !r.outpost));
  function selectAllFiltered() {
    selected = new Set(selectableRows.map((r) => r.id));
  }
  /** quick job configurations for every selected colony */
  function applyPreset(preset: selectors.JobPreset) {
    const s = session().getPlanned();
    if (!s) return;
    for (const row of rows) {
      if (!selected.has(row.id) || row.outpost) continue;
      const groups = selectors.presetJobs(s, row.id, preset);
      if (groups) session().submit('set_jobs', { colonyId: row.id, groups });
    }
  }

  /** "fix food": put the farming load on the most productive selected worlds */
  function applyFixFood() {
    const s = session().getPlanned();
    if (!s) return;
    const ids = rows.filter((r) => selected.has(r.id) && !r.outpost).map((r) => r.id);
    const plan = selectors.fixFoodJobs(s, ids);
    for (const [colonyId, groups] of plan) {
      session().submit('set_jobs', { colonyId, groups });
    }
  }

  type Job = 'farmers' | 'workers' | 'scientists';
  /** reassign within ONE race group — captured colonists keep their own
   * group, so a multi-race colony never gets its groups overwritten */
  function moveJob(row: selectors.ColonyRow, race: number, fromJob: Job, toJob: Job, count = 1) {
    const grp = row.groups.find((g) => g.race === race);
    if (!grp) return;
    const n = Math.min(count, grp[fromJob]);
    if (fromJob === toJob || n <= 0) return;
    const jobs = { race: grp.race, farmers: grp.farmers, workers: grp.workers, scientists: grp.scientists };
    jobs[fromJob] -= n;
    jobs[toJob] += n;
    session().submit('set_jobs', { colonyId: row.id, groups: [jobs] });
  }

  // (the old +/- buttons are gone: dragging citizens replaced them)

  // ---- drag colonists: between job columns, or onto a same-system colony ----
  // Clicking citizen i selects it AND everyone to its right in its own race
  // group; a drag then carries the whole selection (dragging without clicking
  // first does the same from the grabbed icon).
  const JOB_ICONS: Record<Job, string> = { farmers: '🌾', workers: '🔨', scientists: '🧪' };
  let picked = $state<{ colonyId: number; job: Job; race: number; from: number } | null>(null);
  function pickFrom(row: selectors.ColonyRow, job: Job, race: number, i: number) {
    if (picked && picked.colonyId === row.id && picked.job === job && picked.race === race && picked.from === i) {
      picked = null;
    } else {
      picked = { colonyId: row.id, job, race, from: i };
    }
  }
  const isPicked = (row: selectors.ColonyRow, job: Job, race: number, i: number): boolean =>
    !!picked && picked.colonyId === row.id && picked.job === job && picked.race === race && i >= picked.from;
  /** how many citizens a drag starting at icon i of a group carries */
  function grabCount(row: selectors.ColonyRow, job: Job, race: number, i: number): number {
    const grp = row.groups.find((g) => g.race === race);
    if (!grp) return 1;
    const from =
      picked && picked.colonyId === row.id && picked.job === job && picked.race === race && picked.from <= i
        ? picked.from
        : i;
    return grp[job] - from;
  }
  /** icons always overlap a bit (negative kerning), tighter as counts grow */
  const overlapPx = (count: number): number => (count <= 4 ? 3 : count <= 8 ? 6 : count <= 14 ? 8 : 10);
  let drag = $state<{ colonyId: number; job: Job; race: number; count: number } | null>(null);
  let dragOver = $state<{ colonyId: number; job: Job } | null>(null);
  let dragOverColony = $state<number | null>(null);
  let moveNote = $state('');
  let moveNoteTimer: ReturnType<typeof setTimeout> | null = null;
  function note(text: string) {
    moveNote = text;
    if (moveNoteTimer) clearTimeout(moveNoteTimer);
    moveNoteTimer = setTimeout(() => (moveNote = ''), 5000);
  }
  // A drag never needs a pick first: grabbing icon i always carries i plus
  // everyone to its right. The pick (click) is only a keyboard/precision
  // fallback, so a gesture that became a drag must not also toggle the pick —
  // some browsers still deliver click after dragend.
  let dragHappened = false;
  function onDragStart(row: selectors.ColonyRow, job: Job, race: number, i: number, ev: DragEvent) {
    drag = { colonyId: row.id, job, race, count: Math.max(1, grabCount(row, job, race, i)) };
    dragHappened = true;
    ev.dataTransfer?.setData('text/plain', `${row.id}:${job}:${race}:${drag.count}`);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
  }
  /** fires after every drag, dropped or cancelled — without this a drag
   * released outside a valid cell left stale drag state behind */
  function onDragEnd() {
    drag = null;
    dragOver = null;
    dragOverColony = null;
    setTimeout(() => (dragHappened = false), 0);
  }
  function onCitizenClick(row: selectors.ColonyRow, job: Job, race: number, i: number) {
    if (dragHappened) return;
    pickFrom(row, job, race, i);
  }
  function onDrop(row: selectors.ColonyRow, job: Job) {
    if (drag && drag.colonyId === row.id) {
      // androids are hardwired to their built job — within a colony there is
      // nothing to drag them to; relocating to another colony is their one move
      if (drag.race === ANDROID_RACE && job !== drag.job) {
        note('⛔ androids are hardwired to the job they were built for — drop them on another colony to relocate them');
      } else {
        moveJob(row, drag.race, drag.job, job, drag.count);
      }
    } else if (drag && drag.colonyId !== row.id) dropOnColony(row); // job cell of another colony works too
    drag = null;
    picked = null;
    dragOver = null;
    dragOverColony = null;
  }
  /** citizens dropped on a different colony: instant in-system shuttle, or a
   * freighter convoy between systems (5 freighters per colonist, travel time) */
  function dropOnColony(row: selectors.ColonyRow) {
    if (!drag || drag.colonyId === row.id) return;
    const src = allRows.find((r) => r.id === drag!.colonyId);
    if (!src) return;
    const n = drag.count;
    const sameSystem = src.planet.starId === row.planet.starId;
    const res = session().submit('move_colonists', {
      fromColonyId: src.id,
      toColonyId: row.id,
      race: drag.race,
      count: n,
      fromJob: drag.job, // the grabbed citizens leave THEIR job, not scientists
    });
    if (res.error) note(`⛔ ${res.error}`);
    else if (sameSystem) note(`🚚 ${n} colonist${n > 1 ? 's' : ''} shuttled ${src.name} → ${row.name}`);
    else note(`🚚 ${n} colonist${n > 1 ? 's' : ''} boarded freighters ${src.name} → ${row.name} (${5 * n} freighters busy until arrival)`);
  }
  const canDropColony = (row: selectors.ColonyRow): boolean => {
    if (!drag || drag.colonyId === row.id || row.outpost) return false;
    return allRows.some((r) => r.id === drag!.colonyId);
  };

  /** every queue edit surfaces the engine's rejection — a silently ignored
   * error leaves the dropdown face desynced from what is actually building */
  function submitNoted(kind: string, payload: unknown) {
    const res = session().submit(kind, payload);
    if (res.error) note(`⛔ ${res.error}`);
    else if (kind === 'set_build_queue') {
      // ui_streamlining.md P4: a hand-edited queue is player-owned. Pin every
      // item so the governor neither reorders nor replaces it (same lifecycle
      // as map quick-build pins: reconcilePins sheds items as they complete).
      // Clearing the queue clears the pin — the colony returns to autobuild.
      const p = payload as { colonyId: number; items: Array<string | { item: string }> };
      const ids = p.items.map((it) => (typeof it === 'string' ? it : it.item));
      if (ids.length) app.pins[p.colonyId] = ids;
      else delete app.pins[p.colonyId];
      savePerGame();
    }
    return res;
  }

  function setBuild(row: selectors.ColonyRow, item: string) {
    if (!item) return;
    // choosing something already queued PROMOTES one instance of it to the
    // active slot — repeats (ships, projects) are preserved, not collapsed
    const q = row.queueEntries;
    const idx = row.queue.indexOf(item);
    const items =
      idx >= 0
        ? [q[idx]!, ...q.slice(0, idx), ...q.slice(idx + 1)]
        : q.length
          ? [item, ...q.slice(1)]
          : [item];
    submitNoted('set_build_queue', { colonyId: row.id, items });
  }

  /** insert at the very front: the new item builds NOW, and the displaced
   * build keeps its queue place (slot 1) — no more change-head-then-requeue */
  function insertFront(row: selectors.ColonyRow, item: string) {
    if (!item) return;
    submitNoted('set_build_queue', { colonyId: row.id, items: [item, ...row.queueEntries] });
  }

  /** toggle repeat on the active build (ships and repeatable projects): the
   * entry stays at the head after completing and builds another copy */
  function toggleRepeat(row: selectors.ColonyRow) {
    const q = row.queueEntries;
    if (!q.length) return;
    const head = { item: q[0]!.item, ...(q[0]!.repeat ? {} : { repeat: true }) };
    submitNoted('set_build_queue', { colonyId: row.id, items: [head, ...q.slice(1)] });
  }

  function removeQueued(row: selectors.ColonyRow, index: number) {
    submitNoted('set_build_queue', {
      colonyId: row.id,
      items: row.queueEntries.filter((_, i) => i !== index),
    });
  }

  /** promote a queued entry to the active slot; everything else keeps its
   * order (and the displaced head keeps its invested production, sticky) */
  function promoteQueued(row: selectors.ColonyRow, index: number) {
    const q = row.queueEntries;
    if (index <= 0 || index >= q.length) return;
    submitNoted('set_build_queue', {
      colonyId: row.id,
      items: [q[index]!, ...q.slice(0, index), ...q.slice(index + 1)],
    });
  }

  function appendBuild(row: selectors.ColonyRow, item: string) {
    if (!item) return;
    submitNoted('set_build_queue', { colonyId: row.id, items: [...row.queueEntries, item] });
  }

  function buy(row: selectors.ColonyRow) {
    submitNoted('buy_production', { colonyId: row.id });
  }

  function sell(row: selectors.ColonyRow, buildingId: string) {
    submitNoted('sell_building', { colonyId: row.id, buildingId });
  }

  let openBuildings = $state<Set<number>>(new Set());
  function toggleBuildings(id: number) {
    const next = new Set(openBuildings);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    openBuildings = next;
  }

  function parked(row: selectors.ColonyRow): string {
    const entries = Object.entries(row.stickyInvested).filter(([, v]) => v > 0);
    if (!entries.length) return '';
    return entries.map(([k, v]) => `${label(k)}: ${v}`).join(', ');
  }

  // ---- rename + tags ----
  let renaming = $state<number | null>(null);
  let renameText = $state('');
  const focusNow = (el: HTMLElement) => el.focus();
  function startRename(row: selectors.ColonyRow) {
    renaming = row.id;
    renameText = row.name;
  }
  function commitRename(row: selectors.ColonyRow) {
    if (renaming !== row.id) return;
    const name = renameText.trim();
    if (name && name !== row.name) session().submit('rename_colony', { colonyId: row.id, name });
    renaming = null;
  }
  function setTags(row: selectors.ColonyRow, tags: string[]) {
    session().submit('set_colony_tags', { colonyId: row.id, tags });
  }
</script>

<div class="bar">
  <input data-testid="colony-filter" placeholder="filter colonies or tags…" bind:value={filter} style="width:12rem" />
  <button data-testid="select-filtered" title="select every colony matching the current filter" onclick={selectAllFiltered}>
    select all ({selectableRows.length})
  </button>
  <button
    class="mini"
    class:dimoff={!showTags}
    data-testid="toggle-tags"
    title={showTags ? 'hide colony tags' : 'show colony tags'}
    onclick={toggleTags}
  >🏷</button>
  {#if selected.size > 0}
    <span>{selected.size} selected:</span>
    <select
      data-testid="bulk-build"
      value=""
      onchange={(e) => {
        bulkBuild((e.target as HTMLSelectElement).value);
        (e.target as HTMLSelectElement).value = '';
      }}
    >
      <option value="">set build for all…</option>
      {#each bulkOptions as o (o.item)}<option value={o.item}>{label(o.item)}{o.n < o.total ? ` (${o.n} of ${o.total})` : ''}</option>{/each}
    </select>
    <select
      data-testid="bulk-queue-front"
      value=""
      title="insert right after each colony's current build (nothing invested is lost)"
      onchange={(e) => {
        bulkQueue((e.target as HTMLSelectElement).value, 'front');
        (e.target as HTMLSelectElement).value = '';
      }}
    >
      <option value="">⤴ queue next for all…</option>
      {#each bulkOptions as o (o.item)}<option value={o.item}>{label(o.item)}{o.n < o.total ? ` (${o.n} of ${o.total})` : ''}</option>{/each}
    </select>
    <select
      data-testid="bulk-queue-back"
      value=""
      title="append to the end of each selected colony's queue"
      onchange={(e) => {
        bulkQueue((e.target as HTMLSelectElement).value, 'back');
        (e.target as HTMLSelectElement).value = '';
      }}
    >
      <option value="">⤵ queue last for all…</option>
      {#each bulkOptions as o (o.item)}<option value={o.item}>{label(o.item)}{o.n < o.total ? ` (${o.n} of ${o.total})` : ''}</option>{/each}
    </select>
    <span class="presets">
      jobs:
      <button data-testid="preset-research" title="fewest farmers that keep the colony fed AND keep exporting what the rest of the empire needs; everyone else does research" onclick={() => applyPreset('research')}>⚗ research</button>
      <button data-testid="preset-industry" title="fewest farmers that keep the colony fed AND keep exporting what the rest of the empire needs; everyone else works industry" onclick={() => applyPreset('industry')}>⚒ industry</button>
      <button data-testid="preset-feed" title="fix food production: concentrate the empire's farming on the most productive food worlds among the selected colonies" onclick={applyFixFood}>🌾 fix food</button>
    </span>
    <button onclick={() => (selected = new Set())}>clear selection</button>
  {:else if showCitizensTip}
    <span class="dim" data-testid="citizens-tip">
      💡 drag citizens between jobs or onto another colony · ☑ tick rows for bulk builds &amp; presets
      <button class="tipdismiss" data-testid="citizens-tip-dismiss" onclick={dismissCitizensTip}>got it ✕</button>
    </span>
  {/if}
  {#if moveNote}
    <span class="movenote" data-testid="move-note">{moveNote}</span>
  {/if}
  {#if research}
    <span
      class="research"
      data-testid="research-readout"
      title="current research — updates live as you reassign citizens. '% of base' is banked RP vs the listed cost; the real discovery point hides between 1× and 2× that, so once the base is covered this switches to the actual chance of discovering by next turn"
    >
      {#if research.sum.researching}
        🔬 {pretty(research.sum.researching)}{research.sum.researchTarget ? ` → ${pretty(research.sum.researchTarget)}` : ''}
        · {research.accumRP}/{research.sum.researchListedCost ?? '?'} RP{research.sum.researchOddsPct > 0
          ? ` (${research.sum.researchOddsPct}% chance)`
          : research.sum.researchProgressPct !== null
            ? ` (${research.sum.researchProgressPct}% of base)`
            : ''}
        · +{research.sum.researchPerTurn}/turn{research.sum.researchTurnsLeft !== null ? ` · ~${research.sum.researchTurnsLeft}t` : ''}
      {:else}
        🔬 <span class="neg">no research selected</span> · +{research.sum.researchPerTurn}/turn banked
      {/if}
    </span>
  {/if}
</div>

<!-- bugs.md: the autopilot bar cluttered every visit to the colony screen.
     It appears only alongside a full select-all (its scope is every colony
     anyway) — but once ENABLED it stays visible so it can always be seen,
     adjusted and switched off. -->
{#if app.autopilot.enabled || (selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id)))}
  <AutopilotBar />
{/if}

<!-- the table scrolls inside its own container: the page never pans sideways,
     so the tab bar and commit footer stay put, and the checkbox + colony name
     columns stay pinned so you never lose which planet you are editing -->
<div class="tablewrap">
<table data-testid="colony-table">
  <thead>
    <tr>
      <th></th>
      <th class="sortable" onclick={() => sortBy('name')}>Colony {sortKey === 'name' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th>Planet</th>
      <th class="sortable" onclick={() => sortBy('pop')} title="population / capacity (projected growth per turn)">Pop {sortKey === 'pop' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th class="sortable" onclick={() => sortBy('morale')}>Morale {sortKey === 'morale' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th title="farmers">🌱</th>
      <th title="workers">⚒</th>
      <th title="scientists">⚗</th>
      <th class="sortable" onclick={() => sortBy('food')} title="net food">🌾 {sortKey === 'food' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th class="sortable" onclick={() => sortBy('prod')} title="production to the build queue (after pollution)">🔧 {sortKey === 'prod' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th class="sortable" onclick={() => sortBy('sci')} title="research">🔬 {sortKey === 'sci' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th class="sortable" onclick={() => sortBy('bc')} title="income">💰 {sortKey === 'bc' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th title="pollution — lost production this turn">☁️</th>
      <th class="sortable" onclick={() => sortBy('building')}>Building {sortKey === 'building' ? (sortDir > 0 ? '▲' : '▼') : ''}</th>
      <th>Progress</th>
      <th>Buy</th>
      <th>Queue</th>
      <th title="buildings — click 🏛 to inspect and sell">🏛</th>
    </tr>
  </thead>
  <tbody>
    {#each rows as row (row.id)}
      {@const ex = explain(row.id)}
      <tr data-testid="colony-row-{row.id}" class:outpost={row.outpost}>
        <td><input type="checkbox" checked={selected.has(row.id)} onchange={() => toggleSelect(row.id)} /></td>
        <td
          class="name"
          class:shipok={dragOverColony === row.id}
          ondragover={(e) => {
            if (canDropColony(row)) {
              e.preventDefault();
              dragOverColony = row.id;
            }
          }}
          ondragleave={() => {
            if (dragOverColony === row.id) dragOverColony = null;
          }}
          ondrop={(e) => {
            e.preventDefault();
            dropOnColony(row);
            drag = null;
            picked = null;
            dragOver = null;
            dragOverColony = null;
          }}
        >
          {#if renaming === row.id}
            <input
              class="rename"
              data-testid="rename-input-{row.id}"
              bind:value={renameText}
              use:focusNow
              onkeydown={(e) => {
                if (e.key === 'Enter') commitRename(row);
                else if (e.key === 'Escape') renaming = null;
              }}
              onblur={() => commitRename(row)}
              maxlength="24"
            />
          {:else}
            <span
              role="button"
              tabindex="-1"
              title="double-click to rename"
              ondblclick={() => startRename(row)}
              data-testid="colony-name-{row.id}">{row.name}</span>{row.outpost ? ' (outpost)' : ''}
            <button class="mini ghost" data-testid="rename-{row.id}" title="rename colony" onclick={() => startRename(row)}>✏️</button>
            {#if row.leaderName}
              <span class="leader" data-testid="leader-{row.id}" title="governor assigned to this colony (Empires tab)">{row.leaderId ? leaderDisplayName(session().getPlanned()?.seed, row.leaderId, row.leaderName) : row.leaderName}</span>
            {/if}
          {/if}
          {#if showTags}
            <span class="tagsline">
              {#each row.tags as t (t)}
                <button class="tag" data-testid="tag-{row.id}-{t}" title="remove tag {t}" onclick={() => setTags(row, row.tags.filter((x) => x !== t))}>{t}✕</button>
              {/each}
              <select
                class="tagadd"
                data-testid="tag-add-{row.id}"
                value=""
                title="tag this colony"
                onchange={(e) => {
                  const t = (e.target as HTMLSelectElement).value;
                  if (t) setTags(row, [...row.tags, t]);
                  (e.target as HTMLSelectElement).value = '';
                }}
              >
                <option value="">+</option>
                {#each COLONY_TAGS.filter((t) => !row.tags.includes(t)) as t (t)}
                  <option value={t}>{t}</option>
                {/each}
              </select>
            </span>
          {/if}
        </td>
        <td
          class="planet"
          title={planetTitle(row)}
        >
          <span class="pcell">
            <PixelPlanet
              seed={row.planet.id}
              climate={row.planet.climate}
              body={row.planet.body}
              size={26}
              ring={row.planet.minerals === 'ultra_rich' || row.planet.minerals === 'rich'
                ? 'var(--gold)'
                : row.planet.minerals === 'poor' || row.planet.minerals === 'ultra_poor'
                  ? 'var(--text-dim)'
                  : null}
              ringDashed={row.planet.minerals === 'poor' || row.planet.minerals === 'ultra_poor'}
            />
            <span class="pdesc">
              <span class="pclimate">{row.planet.climate} {SIZE_NAMES[row.planet.sizeClass] ?? `s${row.planet.sizeClass}`}</span>
              <span
                class="pminerals"
                class:rich={row.planet.minerals === 'rich' || row.planet.minerals === 'ultra_rich'}
              >{#if row.planet.special && SPECIAL_INFO[row.planet.special]}{SPECIAL_INFO[row.planet.special]!.icon} {/if}{pretty(row.planet.minerals)}{row.planet.gravity !== 'normal' ? ` · ${row.planet.gravity}-g` : ''}</span>
            </span>
          </span>
        </td>
        <td data-testid="pop-{row.id}" title="projected growth next turn: {growthLabel(row.growthK)}">
          {row.popUnits}/{row.maxPop}
          {#if !row.outpost}
            <span class="growth" class:neg={row.growthK < 0}>{growthLabel(row.growthK)}</span>
          {/if}
          {#if row.marineCap > 0}
            <span class="growth" title="marine garrison (barracks trains 1 per 5 turns; a transport boards 4)">🪖{row.marines}/{row.marineCap}</span>
          {/if}
        </td>
        <td>{row.output.moralePct}%</td>
        {#each ['farmers', 'workers', 'scientists'] as const as job (job)}
          <td
            class="jobs"
            class:dropping={dragOver?.colonyId === row.id && dragOver?.job === job}
            ondragover={(e) => {
              if (
                (drag?.colonyId === row.id || canDropColony(row)) &&
                // android farmers synthesize food where nothing grows, so the
                // farmers cell takes their drops even on a dead world
                (job !== 'farmers' || row.farmable || drag?.race === ANDROID_RACE)
              ) {
                e.preventDefault();
                dragOver = { colonyId: row.id, job };
              }
            }}
            ondragleave={() => {
              if (dragOver?.colonyId === row.id && dragOver?.job === job) dragOver = null;
            }}
            ondrop={(e) => {
              e.preventDefault();
              onDrop(row, job);
            }}
          >
            <span
              class="citizens"
              role="group"
              data-testid="{job}-{row.id}"
              data-count={row.jobs[job]}
              title="{row.jobs[job]} {job} — click a citizen to grab them plus everyone to their right, then drag onto another job or any other colony (freighters carry them between systems)"
            >
              {#if job === 'farmers' && !row.farmable}
                <span class="zero" title="nothing grows here — farming is impossible on this world">🚫</span>
              {:else if row.jobs[job] === 0}
                <span class="zero">0</span>
              {/if}
              {#each row.groups as grp (grp.race)}
                {#each Array(grp[job]) as _, i (i)}
                  {#if grp.race === ANDROID_RACE}
                    <!-- androids: hardwired to their built job (0.29.0) — a drag
                         onto another job column is refused, but dropping them on
                         a DIFFERENT colony relocates them in the same job -->
                    <span
                      class="citizen android"
                      class:sel={isPicked(row, job, grp.race, i)}
                      style={i > 0 ? `margin-left:-${overlapPx(row.jobs[job])}px` : ''}
                      draggable="true"
                      role="button"
                      tabindex="-1"
                      title="android {job.slice(0, -1)} — hardwired to this job for life; drag onto another colony to relocate them (they stay a {job.slice(0, -1)} there); consumes 1 production per turn instead of food, immune to morale, houses in compact subterranean compartments"
                      data-testid="android-{job}-{row.id}"
                      onclick={() => onCitizenClick(row, job, grp.race, i)}
                      onkeydown={(e) => e.key === 'Enter' && pickFrom(row, job, grp.race, i)}
                      ondragstart={(e) => onDragStart(row, job, grp.race, i, e)}
                      ondragend={onDragEnd}
                    >🤖</span>
                  {:else}
                    <span
                      class="citizen"
                      class:foreign={grp.race !== session().playerId}
                      class:unrest={grp.unrest}
                      class:sel={isPicked(row, job, grp.race, i)}
                      style={i > 0 ? `margin-left:-${overlapPx(row.jobs[job])}px` : ''}
                      draggable="true"
                      role="button"
                      tabindex="-1"
                      title={grp.race !== session().playerId
                        ? `captured ${grp.raceName} colonist${grp.unrest ? ' — in unrest (−25% output until assimilated)' : ''}`
                        : grp.unrest
                          ? 'in unrest (−25% output until assimilated)'
                          : ''}
                      onclick={() => onCitizenClick(row, job, grp.race, i)}
                      onkeydown={(e) => e.key === 'Enter' && pickFrom(row, job, grp.race, i)}
                      ondragstart={(e) => onDragStart(row, job, grp.race, i, e)}
                      ondragend={onDragEnd}
                    >{JOB_ICONS[job]}</span>
                  {/if}
                {/each}
              {/each}
            </span>
          </td>
        {/each}
        <td
          class:neg={row.output.foodNet < 0}
          class:starving={row.foodLack > 0}
          data-testid="foodnet-{row.id}"
          title={row.output.foodNet < 0
            ? `${ex.farm} — deficit ${-row.output.foodNet}: ${-row.output.foodNet - row.foodLack} covered by freighters${row.foodLack > 0 ? `, ${row.foodLack} UNCOVERED (starvation)` : ''}`
            : ex.farm}
        >{row.output.foodNet >= 0 ? '+' : ''}{row.output.foodNet}{#if row.output.foodNet < 0 && row.foodLack === 0}<span class="fed" title="deficit fully covered by food shipments">✓</span>{/if}</td>
        <td data-testid="prod-{row.id}" title={ex.prod}>
          {row.output.prodToQueue || row.output.prod}{#if row.output.pollution > 0}<span class="poll">−{row.output.pollution}☁</span>{/if}
        </td>
        <td title={ex.sci}>{row.output.research}</td>
        <td title={ex.bc}>{row.output.bcIncome}</td>
        <td class:neg={row.output.pollution > 0} title="production lost to pollution">{row.output.pollution}</td>
        <td>
          <select
            data-testid="build-{row.id}"
            value={row.activeItem ?? ''}
            title={row.activeItem
              ? `${row.name} — ${label(row.activeItem)}${itemDescription(row.activeItem) ? `: ${itemDescription(row.activeItem)}` : ''}`
              : row.name}
            onchange={(e) => setBuild(row, (e.target as HTMLSelectElement).value)}
          >
            <option value="" disabled>— build —</option>
            {#if row.activeItem && !row.buildable.includes(row.activeItem)}
              <option value={row.activeItem}>{label(row.activeItem)}</option>
            {/if}
            <!-- keyed by index: the same non-buildable item can legitimately
                 appear twice (repeat refits, spy past the roster cap) and a
                 duplicate string key crashes the whole table -->
            {#each row.queue.slice(1).filter((q) => !row.buildable.includes(q)) as q, qi (qi)}
              <option value={q}>{label(q)} (queued)</option>
            {/each}
            {#each row.buildable as item (item)}
              <option value={item}>{label(item)}</option>
            {/each}
          </select>
          {#if row.activeItem && itemMayRepeat(row.activeItem)}
            <button
              class="repeat"
              class:on={row.queueEntries[0]?.repeat === true}
              data-testid="repeat-{row.id}"
              title={row.queueEntries[0]?.repeat
                ? 'repeat build is ON: another copy starts each time this completes — click to turn off'
                : 'repeat build: keep building copies of this until you turn it off (or the colony cannot take more)'}
              onclick={() => toggleRepeat(row)}
            >⟳</button>
          {/if}
        </td>
        <td data-testid="progress-{row.id}">
          {#if row.activeItem === 'housing' || row.activeItem === 'trade_goods'}
            ∞
          {:else if row.activeItem}
            <span class="cellbar" title="{row.storedProd}/{row.activeCost}">
              <span class="cellfill" style="width:{row.activeCost > 0 ? Math.min(100, Math.floor((row.storedProd * 100) / row.activeCost)) : 0}%"></span>
            </span>
            {row.storedProd}/{row.activeCost}{row.turnsLeft !== null ? ` (${row.turnsLeft}t)` : ''}
          {:else}
            idle
          {/if}
          {#if stickyMode && parked(row)}
            <span class="parked" title="sticky build: production parked on switched-away items" data-testid="parked-{row.id}">⏸ {parked(row)}</span>
          {/if}
        </td>
        <td>
          {#if row.buyPrice !== null}
            <button data-testid="buy-{row.id}" disabled={!row.canBuy} onclick={() => buy(row)}>
              {row.buyPrice} BC
            </button>
          {/if}
        </td>
        <td>
          <select
            class="queuenow"
            data-testid="queue-now-{row.id}"
            value=""
            title="build NOW: goes in front of {row.activeItem ? label(row.activeItem) : 'the queue'}, which keeps its place and nothing invested is lost"
            onchange={(e) => { insertFront(row, (e.target as HTMLSelectElement).value); (e.target as HTMLSelectElement).value = ''; }}
          >
            <option value="">⚡ build now</option>
            {#each row.buildable as item (item)}
              <option value={item}>{label(item)}</option>
            {/each}
          </select>
          {#each row.queue.slice(1) as q, qi (qi)}
            <span class="queuechip" data-testid="queued-{row.id}-{qi + 1}">
              <button
                class="chipact"
                data-testid="queue-front-{row.id}-{qi + 1}"
                title="move {label(q)} to the front — it builds NOW, everything else keeps its order"
                onclick={() => promoteQueued(row, qi + 1)}
              >⏫</button>
              <span title={label(q)}>{row.queueEntries[qi + 1]?.repeat ? '⟳ ' : ''}{label(q)}</span>
              <button
                class="chipact"
                data-testid="queue-remove-{row.id}-{qi + 1}"
                title="remove {label(q)} from the queue"
                onclick={() => removeQueued(row, qi + 1)}
              >✕</button>
            </span>
          {/each}
          <select data-testid="queue-add-{row.id}" value="" title="append to the back of the queue" onchange={(e) => { appendBuild(row, (e.target as HTMLSelectElement).value); (e.target as HTMLSelectElement).value = ''; }}>
            <option value="">+ queue</option>
            {#each row.buildable as item (item)}
              <option value={item}>{label(item)}</option>
            {/each}
          </select>
        </td>
        <td>
          {#if row.buildings.length}
            <button class="mini" data-testid="buildings-{row.id}" onclick={() => toggleBuildings(row.id)}>
              🏛{row.buildings.length}
            </button>
          {/if}
        </td>
      </tr>
      {#if openBuildings.has(row.id)}
        <tr class="buildingsrow" data-testid="buildings-panel-{row.id}">
          <td colspan="18">
            <div class="chips">
              {#each row.sellables as s (s.id)}
                <span class="chip">
                  {pretty(s.id)}
                  <button
                    class="mini sellbtn"
                    disabled={!row.canSell || s.refund <= 0}
                    title={row.canSell ? `sell for ${s.refund} BC (one sale per colony per turn)` : 'already sold a building here this turn'}
                    data-testid="sell-{row.id}-{s.id}"
                    onclick={() => sell(row, s.id)}
                  >sell {s.refund} BC</button>
                </span>
              {/each}
              {#if !row.canSell}
                <span class="dim">one sale per colony per turn — done for this turn</span>
              {/if}
            </div>
          </td>
        </tr>
      {/if}
    {/each}
  </tbody>
  <tfoot>
    <tr data-testid="totals">
      <td></td>
      <td class="name">Σ {allRows.length} colonies{outpostCount > 0 ? ` · ${outpostCount} outpost${outpostCount > 1 ? 's' : ''} (map)` : ''}</td>
      <td></td>
      <td>{totals.pop} <span class="growth" class:neg={totals.growthK < 0}>{growthLabel(totals.growthK)}</span></td>
      <td></td>
      <td colspan="3"></td>
      <td class:neg={totals.food < 0}>{totals.food >= 0 ? '+' : ''}{totals.food}</td>
      <td>{totals.prod}</td>
      <td>{totals.sci}</td>
      <td>{totals.bc}</td>
      <td class:neg={totals.pollution > 0}>{totals.pollution}</td>
      <td colspan="5"></td>
    </tr>
  </tfoot>
</table>
</div>

<style>
  .bar {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    margin-bottom: 0.4rem;
    flex-wrap: wrap;
  }
  .presets {
    display: inline-flex;
    gap: 0.25rem;
    align-items: center;
  }
  .presets button {
    font-size: 0.78rem;
  }
  .tablewrap {
    overflow-x: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }
  table {
    /* separate, not collapse: collapsed borders detach from position:sticky
       cells and scroll away without them */
    border-collapse: separate;
    border-spacing: 0;
    border-top: 1px solid var(--line);
    border-left: 1px solid var(--line);
    width: 100%;
    font-size: 0.85rem;
  }
  td,
  th {
    border-right: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
    padding: 0.25rem 0.45rem;
    text-align: left;
    white-space: nowrap;
  }
  /* pin the checkbox + colony-name columns while the rest scrolls */
  thead th:nth-child(-n + 2),
  tbody tr:not(.buildingsrow) td:nth-child(-n + 2),
  tfoot td:nth-child(-n + 2) {
    position: sticky;
    z-index: 2;
    background: var(--bg);
    box-sizing: border-box;
  }
  thead th:nth-child(1),
  tbody tr:not(.buildingsrow) td:nth-child(1),
  tfoot td:nth-child(1) {
    left: 0;
    width: 2.1rem;
    min-width: 2.1rem;
  }
  thead th:nth-child(2),
  tbody tr:not(.buildingsrow) td:nth-child(2),
  tfoot td:nth-child(2) {
    left: 2.1rem;
    box-shadow: 2px 0 0 var(--line);
  }
  tfoot td:nth-child(-n + 2) {
    background: var(--panel-2);
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
  }
  /* the Building/Queue columns sit a long way from the colony name: light the
     whole row under the cursor (and while one of its dropdowns has focus) so
     the eye never loses which planet it is working in */
  tbody tr:hover td,
  tbody tr:focus-within td {
    background: color-mix(in srgb, var(--accent) 9%, transparent);
  }
  /* sticky cells need an OPAQUE hover tint or scrolled content bleeds through */
  tbody tr:hover td:nth-child(-n + 2),
  tbody tr:focus-within td:nth-child(-n + 2) {
    background: color-mix(in srgb, var(--accent) 9%, var(--bg));
  }
  tfoot td {
    background: var(--panel-2);
    font-weight: 600;
  }
  .jobs {
    white-space: nowrap;
  }
  .jobs.dropping {
    background: color-mix(in srgb, var(--good) 18%, transparent);
    outline: 1px dashed var(--good);
  }
  .citizens {
    display: inline-flex;
    align-items: center;
    gap: 0;
    min-width: 1.3rem;
    justify-content: center;
  }
  /* selectable text here would let a drag sweep-highlight the emoji glyphs */
  .citizens,
  .citizen,
  .zero {
    -webkit-user-select: none;
    user-select: none;
  }
  .citizen {
    cursor: grab;
    font-size: 0.85rem;
    line-height: 1;
    position: relative;
  }
  .citizen:hover {
    transform: scale(1.25);
    z-index: 2;
  }
  .citizen.sel {
    filter: drop-shadow(0 0 3px var(--accent)) brightness(1.3);
    z-index: 1;
  }
  /* captured colonists of another race: violet ring so they stand apart */
  .citizen.foreign {
    filter: drop-shadow(0 0 2px #c084fc) hue-rotate(45deg);
  }
  .citizen.foreign.sel {
    filter: drop-shadow(0 0 3px var(--accent)) hue-rotate(45deg) brightness(1.3);
  }
  .citizen.unrest {
    filter: drop-shadow(0 0 3px var(--bad)) grayscale(0.5);
  }
  /* androids: teal glow, and a not-allowed cursor — they never change jobs */
  .citizen.android {
    cursor: not-allowed;
    filter: drop-shadow(0 0 2px #22d3ee);
  }
  .zero {
    color: var(--text-dim);
    opacity: 0.5;
    padding: 0 0.2rem;
  }
  .name.shipok {
    background: color-mix(in srgb, var(--accent) 16%, transparent);
    outline: 1px dashed var(--accent);
  }
  .queuechip {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    font-size: 0.72rem;
    padding: 0 0.15rem;
    margin-right: 0.15rem;
    background: var(--panel-3);
    border: 1px solid var(--line);
    border-radius: 8px;
    opacity: 0.85;
  }
  .chipact {
    font-size: 0.72rem;
    padding: 0 0.15rem;
    margin: 0;
    background: none;
    border: none;
    opacity: 0.65;
    cursor: pointer;
  }
  .chipact:hover {
    opacity: 1;
    color: var(--accent);
  }
  .queuenow {
    border-color: var(--accent);
    color: var(--accent-soft);
    margin-right: 0.25rem;
  }
  .repeat {
    padding: 0 0.3rem;
    margin-left: 0.15rem;
    border: 1px solid var(--line);
    border-radius: 8px;
    opacity: 0.6;
  }
  .repeat.on {
    color: var(--accent);
    border-color: var(--accent);
    opacity: 1;
  }
  .queuechip:hover {
    opacity: 1;
    border-color: var(--line-bright);
  }
  .movenote {
    font-size: 0.8rem;
    color: var(--accent-soft);
  }
  /* live research readout: pushed to the bar's right edge, always in view
     while dragging scientists around */
  .research {
    margin-left: auto;
    font-size: 0.8rem;
    color: var(--accent-soft);
    white-space: nowrap;
  }
  .mini {
    padding: 0 0.35rem;
    margin: 0 0.1rem;
  }
  .neg {
    color: var(--bad);
  }
  /* uncovered food shortage: starvation is imminent, shout louder than a
     plain deficit that freighters are quietly covering */
  td.starving {
    font-weight: 700;
    text-decoration: underline wavy var(--bad);
  }
  .fed {
    color: var(--good, var(--good));
    font-size: 0.75em;
    margin-left: 0.15em;
  }
  .growth {
    font-size: 0.75rem;
    color: var(--good);
    margin-left: 0.2rem;
  }
  .growth.neg {
    color: var(--bad);
  }
  .poll {
    font-size: 0.72rem;
    color: var(--bad);
    margin-left: 0.25rem;
    opacity: 0.9;
  }
  .cellbar {
    display: inline-block;
    vertical-align: middle;
    width: 3.2rem;
    height: 0.4rem;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 3px;
    overflow: hidden;
    margin-right: 0.3rem;
  }
  .cellfill {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #24418a, var(--accent));
  }
  .dim {
    opacity: 0.65;
  }
  .tipdismiss {
    font-size: 0.72rem;
    padding: 0 0.3rem;
    margin-left: 0.4rem;
  }
  /* planet cell: pixel sprite + compact two-line spec — hover for the rest */
  td.planet {
    font-size: 0.6rem;
    max-width: 8rem;
    overflow: hidden;
  }
  .pcell {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .pdesc {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
  }
  .pclimate {
    text-transform: capitalize;
    color: var(--text);
    white-space: nowrap;
  }
  .pminerals {
    text-transform: capitalize;
    color: var(--text-dim);
    white-space: nowrap;
  }
  .pminerals.rich {
    color: var(--gold);
  }
  .parked {
    display: block;
    color: var(--gold);
    font-size: 0.75rem;
  }
  .name {
    font-weight: 600;
  }
  .name .ghost {
    opacity: 0;
    border: none;
    background: transparent;
  }
  .leader {
    color: var(--text-dim);
    font-weight: 400;
    font-size: 0.72rem;
    margin-left: 0.3rem;
  }
  .name:hover .ghost {
    opacity: 0.7;
  }
  .rename {
    width: 8rem;
  }
  .tagsline {
    display: inline-flex;
    gap: 0.2rem;
    margin-left: 0.3rem;
    align-items: center;
  }
  .tag {
    font-size: 0.68rem;
    padding: 0 0.3rem;
    background: var(--panel-3);
    border: 1px solid var(--line);
    border-radius: 8px;
    color: var(--accent-soft);
  }
  .tagadd {
    font-size: 0.6rem;
    max-width: 1.5rem;
    padding: 0 0.1rem;
    border: none;
    background: transparent;
    opacity: 0.4;
  }
  .tagadd:hover {
    opacity: 1;
  }
  .dimoff {
    opacity: 0.35;
  }
  .outpost {
    opacity: 0.6;
  }
  select {
    max-width: 11rem;
  }
  .buildingsrow td {
    background: var(--panel);
  }
  .chips {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .chip {
    background: var(--panel-3);
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 0.15rem 0.45rem;
    text-transform: capitalize;
  }
  .sellbtn {
    margin-left: 0.35rem;
    font-size: 0.75rem;
  }
</style>
